import React, { useEffect, useState } from "react";
import axios from "axios";
import { Clock3 } from "lucide-react";
import { useLang } from "../i18n/LangProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    label: "Wait-time policy",
    grace: (m) => `${m} min included`,
    rate: (chf) => `CHF ${chf} per 15 min after`,
  },
  de: {
    label: "Wartezeit-Politik",
    grace: (m) => `${m} Min inklusive`,
    rate: (chf) => `CHF ${chf} pro 15 Min danach`,
  },
  pt: {
    label: "Política de espera",
    grace: (m) => `${m} min incluídos`,
    rate: (chf) => `CHF ${chf} por 15 min após`,
  },
  fr: {
    label: "Politique d'attente",
    grace: (m) => `${m} min inclus`,
    rate: (chf) => `CHF ${chf} par 15 min ensuite`,
  },
  it: {
    label: "Politica di attesa",
    grace: (m) => `${m} min inclusi`,
    rate: (chf) => `CHF ${chf} ogni 15 min oltre`,
  },
};

/**
 * Reads live wait-time policy from the public `/api/business-rules` endpoint
 * and renders it as a compact note. Falls back gracefully if the endpoint is
 * unreachable — silently hides.
 *
 * Variants:
 *  • `compact` (default) — one-line note, ideal for booking sidebar.
 *  • `card` — bordered block for confirmation pages.
 */
export const WaitTimeNote = ({ variant = "compact" }) => {
  const { lang } = useLang();
  const [rules, setRules] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/business-rules`)
      .then((r) => { if (!cancelled) setRules(r.data); })
      .catch(() => { /* silently ignore */ });
    return () => { cancelled = true; };
  }, []);

  if (!rules) return null;
  const grace = Number(rules.wait_time_grace_min) || 0;
  const rate = Number(rules.wait_time_rate_chf_per_15min) || 0;
  if (grace <= 0 && rate <= 0) return null;

  const c = COPY[lang] || COPY.en;

  if (variant === "card") {
    return (
      <div data-testid="wait-time-card" className="border border-brand-border p-5 flex items-start gap-3">
        <Clock3 size={16} className="text-bronze mt-0.5 shrink-0" />
        <div>
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-1">{c.label}</p>
          <p className="text-sm text-brand-platinum leading-relaxed">
            {c.grace(grace)}
            {rate > 0 && <> · <span className="text-brand-silver">{c.rate(rate)}</span></>}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wait-time-note" className="flex items-center gap-2 text-[11px] text-brand-silver">
      <Clock3 size={11} className="text-bronze" />
      <span>
        <span className="text-brand-platinum">{c.grace(grace)}</span>
        {rate > 0 && <> · {c.rate(rate)}</>}
      </span>
    </div>
  );
};

export default WaitTimeNote;
